import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    UserProfile, SavedOutfit, Lookbook, SavedContent,
    WardrobeItem, OutfitLayer, SceneConfiguration
} from '@shared/types';
import {
    getUserProfile, saveUserProfile, getUserSavedOutfits, saveUserSavedOutfits,
    getUserLookbooks, saveUserLookbooks, getUserSavedContent, saveUserSavedContent,
    getUserFavorites, saveUserFavorites, getUserWardrobe, saveUserWardrobe
} from '@shared/services/userService';
import { defaultWardrobe } from '@shared/data/wardrobe';
import { POSE_CATEGORIES } from '@shared/constants';

interface AppContextType {
    currentUserId: string;
    setCurrentUserId: (id: string) => void;
    userProfile: UserProfile;
    setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
    wardrobe: WardrobeItem[];
    setWardrobe: React.Dispatch<React.SetStateAction<WardrobeItem[]>>;
    savedOutfits: SavedOutfit[];
    setSavedOutfits: React.Dispatch<React.SetStateAction<SavedOutfit[]>>;
    lookbooks: Lookbook[];
    setLookbooks: React.Dispatch<React.SetStateAction<Lookbook[]>>;
    savedContent: SavedContent[];
    setSavedContent: React.Dispatch<React.SetStateAction<SavedContent[]>>;
    favoritedOutfitIds: Set<string>;
    setFavoritedOutfitIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    isDarkMode: boolean;
    setIsDarkMode: (isDark: boolean) => void;
    allUsers: string[];
    setAllUsers: React.Dispatch<React.SetStateAction<string[]>>;

    // Actions
    updateUserProfile: (profile: UserProfile | ((prev: UserProfile) => UserProfile)) => void;
    toggleFavoriteOutfit: (id: string) => void;
    toggleSocialChannel: (channelId: string, isConnected: boolean) => void;
    addSavedContent: (content: Omit<SavedContent, 'id' | 'createdAt'>) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUserId, setCurrentUserId] = useState<string>(() => {
        try {
            return localStorage.getItem('codina_vto_current_user_id') || 'guest-user';
        } catch {
            return 'guest-user';
        }
    });
    const [userProfile, setUserProfile] = useState<UserProfile>({
        measurements: {},
        membershipLevel: 'basic',
        saveImageQuality: 'medium',
        connectedAccounts: {},
        generationCount: 0,
        generationLimitResetDate: new Date().toISOString(),
        favoritePoses: [],
        accountStatus: 'active'
    });
    const [wardrobe, setWardrobe] = useState<WardrobeItem[]>(defaultWardrobe);
    const [savedOutfits, setSavedOutfits] = useState<SavedOutfit[]>([]);
    const [lookbooks, setLookbooks] = useState<Lookbook[]>([]);
    const [savedContent, setSavedContent] = useState<SavedContent[]>([]);
    const [favoritedOutfitIds, setFavoritedOutfitIds] = useState<Set<string>>(new Set());
    const [isDarkMode, setIsDarkMode] = useState(true);
    const [allUsers, setAllUsers] = useState<string[]>(['guest-user', 'admin-user']);

    // Load data when user changes
    useEffect(() => {
        const loadUserData = async () => {
            localStorage.setItem('codina_vto_current_user_id', currentUserId);
            
            const [profile, savedWardrobe, outfits, books, content, favorites] = await Promise.all([
                getUserProfile(currentUserId),
                getUserWardrobe(currentUserId),
                getUserSavedOutfits(currentUserId),
                getUserLookbooks(currentUserId),
                getUserSavedContent(currentUserId),
                getUserFavorites(currentUserId)
            ]);

            setUserProfile(profile);

            let adminItems: WardrobeItem[] = [];
            try {
                adminItems = JSON.parse(localStorage.getItem('admin_wardrobe_items') || '[]');
            } catch (e) {
                console.error('Failed to load admin items', e);
            }

            setWardrobe([...defaultWardrobe, ...savedWardrobe, ...adminItems]);
            setSavedOutfits(outfits);
            setLookbooks(books);
            setSavedContent(content);
            setFavoritedOutfitIds(new Set(favorites));
        };

        loadUserData();
    }, [currentUserId]);

    // Save wardrobe when it changes
    useEffect(() => {
        const saveWardrobe = async () => {
            // Only save custom items (not in defaultWardrobe AND not in adminItems)
            let adminItems: WardrobeItem[] = [];
            try {
                adminItems = JSON.parse(localStorage.getItem('admin_wardrobe_items') || '[]');
            } catch { }

            const customItems = wardrobe.filter(item =>
                !defaultWardrobe.some(d => d.id === item.id) &&
                !adminItems.some(a => a.id === item.id)
            );
            await saveUserWardrobe(currentUserId, customItems);
        };

        saveWardrobe();
    }, [wardrobe, currentUserId]);

    const updateUserProfile = useCallback(async (p: UserProfile | ((prev: UserProfile) => UserProfile)) => {
        let next: UserProfile | null = null;
        setUserProfile(prev => {
            next = typeof p === 'function' ? p(prev) : p;
            return next;
        });
        
        // Wait for state calculation and save
        if (next) {
            try {
                await saveUserProfile(currentUserId, next);
            } catch (e) {
                console.error('Failed to save profile', e);
            }
        }
    }, [currentUserId]);

    const toggleFavoriteOutfit = useCallback(async (id: string) => {
        let nextFavorites: string[] = [];
        setFavoritedOutfitIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            nextFavorites = Array.from(newSet);
            return newSet;
        });
        
        try {
            await saveUserFavorites(currentUserId, nextFavorites);
        } catch (e) {
            console.error('Failed to save favorites', e);
        }
    }, [currentUserId]);

    const toggleSocialChannel = useCallback((channelId: string, isConnected: boolean) => {
        updateUserProfile(prev => {
            const currentChannels = prev.socialChannels || [];
            const updatedChannels = isConnected
                ? [...new Set([...currentChannels, channelId])]
                : currentChannels.filter(id => id !== channelId);
            return { ...prev, socialChannels: updatedChannels };
        });
    }, [updateUserProfile]);

    const addSavedContent = useCallback(async (content: Omit<SavedContent, 'id' | 'createdAt'>) => {
        const newContent: SavedContent = {
            ...content,
            id: `content-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            createdAt: new Date().toISOString()
        };
        let updated: SavedContent[] = [];
        setSavedContent(prev => {
            updated = [newContent, ...prev];
            return updated;
        });
        
        try {
            await saveUserSavedContent(currentUserId, updated);
        } catch (e) {
            console.error('Failed to save content', e);
        }
    }, [currentUserId]);

    const value: AppContextType = {
        currentUserId,
        setCurrentUserId,
        userProfile,
        setUserProfile,
        wardrobe,
        setWardrobe,
        savedOutfits,
        setSavedOutfits,
        lookbooks,
        setLookbooks,
        savedContent,
        setSavedContent,
        favoritedOutfitIds,
        setFavoritedOutfitIds,
        isDarkMode,
        setIsDarkMode,
        allUsers,
        setAllUsers,
        updateUserProfile,
        toggleFavoriteOutfit,
        toggleSocialChannel,
        addSavedContent
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
